/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/blacklistApi.ts
 🗓️ Created: 2025-01-21
 🧩 Description:
    API functions for Blacklist.
    Centralizes all API calls related to blacklist entries.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  BlacklistEntry,
  ListBlacklistEntriesResponse,
  BlacklistEntryPayload,
} from '../types';

/**
 * Fetch Blacklist entries with pagination and search
 */
export async function fetchBlacklistEntries(params: {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
  domainId?: number;
}): Promise<ListBlacklistEntriesResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
    ...(params.search && { search: params.search }),
    ...(params.domainId != null && { domainId: params.domainId.toString() }),
  });

  const url = `${buildApiUrl('/api/telephony/blacklist')}?${queryParams}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to fetch Blacklist entries');
  }

  return result;
}

/**
 * Fetch single Blacklist entry by ID or UUID
 */
export async function fetchBlacklistEntry(
  id: string | number,
): Promise<BlacklistEntry> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/blacklist/${id}`),
    {
      method: 'GET',
      headers: getApiHeaders(),
    },
  );

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to fetch Blacklist entry');
  }

  return result.data;
}

/**
 * Create new Blacklist entry
 */
export async function createBlacklistEntry(
  payload: BlacklistEntryPayload,
): Promise<BlacklistEntry> {
  const response = await fetch(
    buildApiUrl('/api/telephony/blacklist'),
    {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || 'Failed to create Blacklist entry');
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to create Blacklist entry');
  }

  return result.data;
}

/**
 * Update Blacklist entry
 */
export async function updateBlacklistEntry(
  id: string | number,
  payload: BlacklistEntryPayload,
): Promise<BlacklistEntry> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/blacklist/${id}`),
    {
      method: 'PUT',
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || 'Failed to update Blacklist entry');
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to update Blacklist entry');
  }

  return result.data;
}

/**
 * Delete Blacklist entry
 */
export async function deleteBlacklistEntry(id: string | number): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/blacklist/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(),
    },
  );

  const result = await response.json().catch(() => null);

  if (!response.ok || !result?.success) {
    const message = result?.message || 'Failed to delete Blacklist entry';
    throw new Error(message);
  }
}

